import { create } from 'zustand';
import type { AppUser } from '@/types/auth';
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle as fbSignInWithGoogle,
  signInWithGoogleAccessToken as fbSignInWithGoogleAccessToken,
  signOut as fbSignOut,
  onAuthChange,
  updateUserProfile,
  sendVerificationEmail,
} from '@/lib/firebase/auth';
import { getDocument, setDocument, updateDocument, queryDocuments, getDocFromServer } from '@/lib/firebase/firestore';
import { httpsCallable } from '@/lib/firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { deleteField, doc } from 'firebase/firestore';
import { db, functions, auth as firebaseAuth, setZohoFixedIpForUser, setZohoFixedIpReady } from '@/config/firebase';
import { COLLECTIONS, ADMIN_UIDS } from '@/config/constants';
import { useAppStore } from '@/stores/app-store';

// ── Session storage keys for impersonation persistence across page reloads ──
const SESSION_KEY_UID = 'ua_impersonated_uid';
const SESSION_KEY_EMAIL = 'ua_impersonated_email';

// ── Module-level auth listener tracking ──
// Survives React StrictMode cleanup cycles. The Firebase onAuthStateChanged
// listener is set up once and never unsubscribed by React effect cleanup.
let authListenerActive = false;


/** localStorage keys that hold user-specific cached data (cleared on impersonation change) */
const USER_SPECIFIC_LS_KEYS = [
  'ulgebra_app_preferences',
  'ulgebra_lookup_enabled_modules',
  'ulgebra_lookup_visible_fields',
  'ulgebra_lookup_field_order',
  'ulgebra_lookup_show_by_default',
  'ulgebra_fcm_token',
  'ulgebra_notification_permission',
  // Half-typed compose message and the cached voice numbers — both belong to
  // whoever was signed in, so they must not survive into the next account.
  'ulgebra_draft_message',
  'ulgebra_voice_numbers_cache',
];

/** Admin who shared their TPA credentials with the current user */
export interface InvitedAdmin {
  invitedAdminUserID: string;
  invitedAdminUserEmail: string;
  invitedAdminUserName: string;
  invitedAdminUserPic?: string;
  currentSelected?: boolean;
  /** `'rejected'` once the user declines the invite. Set by
   *  `rejectAdminInvite` on both the user's own copy and (best-effort) the
   *  admin's, so the admin's Organization list can show it. Cleared by a
   *  re-add, since that overwrites the whole doc. */
  inviteStatus?: 'rejected';
  rejectedTime?: number;
  /** The admin's SaaS org id, stamped by newer admin builds when the invite
   *  is written (`addUserToOrg`). Lets a shared user read shared docs under
   *  the ADMIN's org without a backend round-trip (KAN-178). */
  saasOrgID?: string;
  /** When the admin wrote the invite doc. Bumped on every add, so a
   *  remove-then-re-add produces a strictly newer value than the one the
   *  user accepted — that's how `verifySelectedAdminMembership` spots an
   *  invite that has to be accepted again. */
  createdTime?: number;
  /** appCode the invite doc was actually found under — either the current
   *  `extensionName` or the `{tpa}forpage` fallback. Used when accepting so
   *  UPDATE_USER_TOKEN_CLAIMS authorizes against the same appCode the admin
   *  added the user under (otherwise forpage-only invites fail with
   *  "Not authorized" and no claim is written). */
  sourceAppCode?: string;
}

/**
 * localStorage key holding the `createdTime` of the invite the user actually
 * accepted. Kept next to `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID`
 * (same `{ext}_{uid}` scoping) and mirrored into personal settings so it
 * survives a cache wipe or a different browser.
 */
function acceptedInviteTimeKey(ext: string, uid: string): string {
  return `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_INVITE_TIME_${ext}_${uid}`;
}

/** Read the accepted-invite timestamp for the current user/extension. */
export function readAcceptedInviteTime(): number | null {
  const uid = useAuthStore.getState().user?.uid;
  const ext = useAppStore.getState().extensionName;
  if (!uid || !ext) return null;
  try {
    const raw = localStorage.getItem(acceptedInviteTimeKey(ext, uid));
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Write (or clear, with `null`) the accepted-invite timestamp. */
export function writeAcceptedInviteTime(time: number | null): void {
  const uid = useAuthStore.getState().user?.uid;
  const ext = useAppStore.getState().extensionName;
  if (!uid || !ext) return;
  try {
    const key = acceptedInviteTimeKey(ext, uid);
    if (time == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(time));
  } catch { /* localStorage unavailable */ }
}

/** Legacy docs scrub falsy fields to the literal string 'null', and the
 *  impersonation flow can store a uid in an email field — treat anything
 *  without an '@' as "no email known". */
const looksLikeEmail = (v: string | null | undefined): v is string => !!v && v.includes('@');

/** One failed invited-admin fetch must not block retries for the session. */
let _invitedAdminFetchFailed = false;

/** Guards against re-entrant revocation (the check runs from several places). */
let revokeInFlight = false;
/** `{uid}:{ext}:{adminUid}:{acceptedStamp}` of the last membership check, so
 *  the several restore paths that trigger it don't each cost a Firestore read.
 *  Reset to null when a read fails, so the next caller retries. */
let lastMembershipCheck: string | null = null;

interface AuthStore {
  user: AppUser | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;

  /** Impersonation — admin-only: view the app as another user for debugging */
  impersonatedUserId: string | null;
  impersonatedUserEmail: string | null;

  /** Shared admin access — user is using an admin's TPA credentials */
  selectedAdminUserId: string | null;
  selectedAdminUserEmail: string | null;
  /** True once this load knows whether the user is on a shared admin */
  selectedAdminResolved: boolean;
  invitedAdminList: Record<string, InvitedAdmin> | null;

  /** Bumped after token claims are refreshed — triggers RTDB listener re-attach */
  tokenClaimsVersion: number;

  /** Email suggested by the CRM environment (used for account mismatch detection) */
  suggestedLoginEmail: string | null;
  setSuggestedLoginEmail(email: string | null): void;
  /** Dismiss account mismatch warning for the current user (persists in localStorage) */
  dismissAccountMismatch(): void;

  /** True when signIn detected the account doesn't exist — UI should show name input */
  needsSignUp: boolean;
  /** Email/password stored temporarily when needsSignUp is true */
  pendingSignUpEmail: string | null;
  pendingSignUpPassword: string | null;
  /** True after email sign-up when email is not yet verified */
  needsEmailVerification: boolean;

  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, displayName: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  /** Finish Google sign-in from an access token the browser extension obtained
   *  on our behalf (embedded panels, where the popup flow cannot work).
   *  Rethrows so the calling form can show the failure. */
  signInWithGoogleAccessToken(accessToken: string): Promise<void>;
  /** Signs out and hard-navigates so the next login starts from a clean slate.
   *  `redirectTo` replaces the location instead of reloading it — use it when
   *  the caller would otherwise navigate after the reload has already fired.
   *  `reload: false` keeps the page alive for callers that render their own
   *  sign-in UI afterwards; the module/cache teardown still happens. */
  signOut(options?: { redirectTo?: string; reload?: boolean }): Promise<void>;
  setUser(user: AppUser | null): void;
  setError(error: string | null): void;
  updatePhoto(photoURL: string): Promise<void>;
  /** Refresh the server-side UA_MAIL_VS_ID cache for the current user and
   *  invalidate client-side caches so stale displayName/photoURL/email is
   *  not served by any reader. Fire-and-forget safe. */
  syncProfileCache(): Promise<void>;
  initializeAuth(): () => void;

  /** Set impersonation target (admin only). Pass null to clear. */
  setImpersonation(userId: string | null, email?: string | null): void;

  /** Set shared admin (any user invited by an admin). Pass null to clear.
   *  Persists to personal settings + TPA tokens + uaOrgAdmins, then clears caches + reloads. */
  setSelectedAdmin(userId: string | null, email?: string | null): Promise<void>;

  /** Set shared admin state without triggering reload/cache-clear.
   *  Used during settings restore to silently hydrate state on load. */
  setSelectedAdminSilent(userId: string | null, email?: string | null): void;

  /** Fetch the list of admins who have invited the current user.
   *  Called lazily (when TPA auth fails); results cached in-memory. */
  fetchInvitedAdminList(opts?: { force?: boolean }): Promise<void>;

  /** Decline an organization invite. Marks the invite doc under the user's own
   *  email as rejected — the admin's Organization list reads those docs, so
   *  the outcome shows up there too. Leaves the org if this was the active
   *  one. Throws when nothing could be written, so the caller can surface the
   *  failure rather than pretend it worked. */
  rejectAdminInvite(adminUid: string): Promise<void>;

  /** Drop shared-admin access when the selected admin has removed this user,
   *  or re-invited them after a removal (a re-invite has to be accepted
   *  again). Reads the live `uaOrgAdmins` invite doc — the only place the
   *  removal is recorded — and, when it's gone or newer than the one the user
   *  accepted, runs the full `setSelectedAdmin(null)` teardown (localStorage,
   *  personal settings, TPA tokens, token claims, caches, reload). No-op when
   *  nothing is stale, and deduped so the several restore paths that call it
   *  cost one read between them. Safe to call repeatedly. */
  verifySelectedAdminMembership(): Promise<void>;
}

/** Fields of ulgebraUsers/{uid} that the app reads after login. */
export interface UlgebraUserDocFields {
  isAdmin?: boolean;
  inAppCodes?: string[];
  preferredCurrency?: string;
  useZohoFixedIp?: boolean;
  timezoneText?: string;
  ph?: string;
}

// One read of ulgebraUsers/{uid} per page load, shared by the login check and
// the background sync in use-app-init. Read from the server: getDocument is
// cache-first, so a flag support just set would only show after a second reload.
let userDocLoad: { uid: string; promise: Promise<UlgebraUserDocFields | null> } | null = null;

export function loadUserDocOnce(uid: string): Promise<UlgebraUserDocFields | null> {
  if (!userDocLoad || userDocLoad.uid !== uid) {
    const promise = getDocFromServer(doc(db, COLLECTIONS.USERS, uid))
      .then((snap) => (snap.exists() ? (snap.data() as UlgebraUserDocFields) : null))
      .catch(() => getDocument<UlgebraUserDocFields>(COLLECTIONS.USERS, uid)); // offline: use the cache
    userDocLoad = { uid, promise };
    // Don't keep a failed read, so the background sync can try again.
    promise.catch(() => {
      if (userDocLoad?.promise === promise) userDocLoad = null;
    });
  }
  return userDocLoad.promise;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  initialized: false,

  impersonatedUserId: null,
  impersonatedUserEmail: null,

  needsSignUp: false,
  pendingSignUpEmail: null,
  pendingSignUpPassword: null,
  needsEmailVerification: false,
  suggestedLoginEmail: null,
  setSuggestedLoginEmail: (email) => set({ suggestedLoginEmail: email?.toLowerCase() || null }),
  dismissAccountMismatch: () => {
    const user = get().user;
    const suggested = get().suggestedLoginEmail;
    if (user?.email && suggested) {
      try { localStorage.setItem(`ua_ignore_account_mismatch_for_${user.email.toLowerCase()}`, suggested); } catch { /* */ }
    }
    set({ suggestedLoginEmail: null });
  },

  selectedAdminUserId: null,
  selectedAdminUserEmail: null,
  selectedAdminResolved: false,
  invitedAdminList: null,
  tokenClaimsVersion: 0,

  signIn: async (email, password) => {
    set({ loading: true, error: null, needsSignUp: false });
    try {
      const credential = await signInWithEmail(email, password);
      const user = mapFirebaseUser(credential.user);
      await ensureUserDoc(user);
      set({ user, loading: false, needsEmailVerification: !credential.user.emailVerified });
    } catch (error: any) {
      const code = error?.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        // Account doesn't exist — prompt for name before creating
        set({
          loading: false,
          needsSignUp: true,
          pendingSignUpEmail: email,
          pendingSignUpPassword: password,
          error: null,
        });
        return;
      }
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Sign in failed',
      });
    }
  },

  signUp: async (email, password, displayName) => {
    set({ loading: true, error: null });
    try {
      const credential = await signUpWithEmail(email, password);
      // Set display name on the Firebase user
      await updateUserProfile({ displayName });
      // Send verification email
      await sendVerificationEmail().catch(() => {});
      const user = mapFirebaseUser({ ...credential.user, displayName } as any);
      await ensureUserDoc(user);
      set({ user, loading: false, needsSignUp: false, pendingSignUpEmail: null, pendingSignUpPassword: null, needsEmailVerification: !credential.user.emailVerified });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Sign up failed',
      });
    }
  },

  signInWithGoogle: async () => {
    set({ loading: true, error: null });
    try {
      const credential = await fbSignInWithGoogle();
      const user = mapFirebaseUser(credential.user);
      await ensureUserDoc(user);
      set({ user, loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Google sign in failed',
      });
    }
  },

  signInWithGoogleAccessToken: async (accessToken) => {
    // Deliberately no `loading` flip: AuthGuard swaps the login form for a
    // skeleton while loading is true, unmounting the very form that has to
    // render a failure. The form's own submitting state drives its spinner.
    set({ error: null });
    try {
      const credential = await fbSignInWithGoogleAccessToken(accessToken);
      const user = mapFirebaseUser(credential.user);
      await ensureUserDoc(user);
      set({ user });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Google sign in failed' });
      throw error;
    }
  },

  signOut: async (options) => {
    // Whether we're signing out a real session. Guards the reload below so a
    // no-op sign-out (already signed out) can't bounce the page forever —
    // logout-page calls signOut() on mount, so an unconditional reload would
    // remount it and sign out again on every pass.
    const hadUser = !!get().user;
    try {
      // Clear cached user immediately (don't wait for auth callback)
      try {
        localStorage.removeItem('ua_cache_user');
        localStorage.removeItem('ua_cache_user_timestamp');
        // Per-user data that isn't scoped by uid. `ulgebra_app_preferences`
        // in particular drives `hasCachedSettings` in use-app-init, so leaving
        // it behind makes the next login mark settings loaded from the
        // PREVIOUS account's preferences (signature, default sender, …).
        for (const key of USER_SPECIFIC_LS_KEYS) {
          localStorage.removeItem(key);
        }
        console.log('[Auth] signOut: Cleared cached user and user-specific keys');
      } catch {
        // localStorage may be unavailable
      }

      // Clear impersonation on sign out
      try {
        sessionStorage.removeItem(SESSION_KEY_UID);
        sessionStorage.removeItem(SESSION_KEY_EMAIL);
      } catch { /* sessionStorage unavailable */ }
      // KAN-135: a pending re-auth history wipe must not follow the next user
      // who signs in on this tab.
      const { clearTPAReauthWipe } = await import('@/modules/tpa/tpa-reauth-cleanup');
      clearTPAReauthWipe();

      await fbSignOut();
      set({
        user: null, error: null,
        needsEmailVerification: false, needsSignUp: false, pendingSignUpEmail: null, pendingSignUpPassword: null,
        impersonatedUserId: null, impersonatedUserEmail: null,
        selectedAdminUserId: null, selectedAdminUserEmail: null, selectedAdminResolved: false, invitedAdminList: null,
      });

      if (hadUser) {
        // Signing out clears auth state but leaves everything the previous
        // session built in memory: the SaaS/TPA module instances (nothing
        // calls moduleStore.unloadModules()) and the proxy L1 response cache.
        // Logging in as someone else in the same tab then renders the PREVIOUS
        // account's CRM identity — correct only after a manual reload.
        // Dropping the API caches + reloading gives the next login the same
        // clean slate a reload already produces today.
        try {
          const { clearProxyCache } = await import('@/lib/api/proxy');
          clearProxyCache();
          // clearProxyCache only drops the `proxy_cache` store. `org_data`
          // and `templates` are keyed by extensionName with no uid
          // (`senders_${ext}`, templates(ext, channel), CRM record context),
          // so leaving them makes the next account read this one's senders,
          // conversations and record context. Same three stores the
          // impersonation hand-off below clears, for the same reason.
          const { idbCache } = await import('@/lib/cache/idb-cache');
          // Bounded: openDB() has no onblocked handler, so if another tab
          // holds an older-version connection the open never settles and this
          // await would stall the reload — sign-out would appear to hang.
          // Losing the clear is far better than losing the sign-out; the
          // reload still drops every in-memory cache either way.
          await Promise.race([
            Promise.all([
              idbCache.clear('org_data').catch(() => {}),
              idbCache.clear('templates').catch(() => {}),
              idbCache.clear('proxy_cache').catch(() => {}),
            ]),
            new Promise((resolve) => setTimeout(resolve, 1500)),
          ]);
        } catch { /* non-fatal — the reload is what actually matters */ }

        if (options?.reload === false) {
          // Caller stays on the page (e.g. the account-mismatch banner shows
          // an inline sign-in form), so do by hand what the reload would have
          // done: dispose the SaaS/TPA modules built for the old account.
          // Without this the next sign-in reuses them and renders the previous
          // account's CRM identity — the exact bug the reload exists to avoid.
          try {
            const { useModuleStore } = await import('./module-store');
            useModuleStore.getState().unloadModules();
          } catch { /* non-fatal */ }
          try {
            const { clearLocalPrefOverrides } = await import('./settings-store');
            clearLocalPrefOverrides();
          } catch { /* non-fatal */ }
          return;
        }

        // `redirectTo` avoids a wasted cycle for callers that would navigate
        // anyway (logout-page): reloading first would land back on the same
        // route and only redirect on the second pass.
        if (options?.redirectTo) window.location.replace(options.redirectTo);
        else window.location.reload();
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Sign out failed',
      });
    }
  },

  setUser: (user) => set({ user }),
  setError: (error) => set({ error }),

  setImpersonation: (userId, email) => {
    const prev = get().impersonatedUserId;
    if (userId) {
      // Only allow admins to impersonate
      const user = get().user;
      if (!user || !ADMIN_UIDS.has(user.uid)) {
        console.warn('[Auth] Non-admin attempted impersonation');
        return;
      }
      set({ impersonatedUserId: userId, impersonatedUserEmail: email ?? null });
      // Persist to sessionStorage so impersonation survives page reload
      try {
        sessionStorage.setItem(SESSION_KEY_UID, userId);
        if (email) sessionStorage.setItem(SESSION_KEY_EMAIL, email);
        else sessionStorage.removeItem(SESSION_KEY_EMAIL);
      } catch { /* sessionStorage unavailable */ }
      console.log('[Auth] Impersonating user:', userId, email ?? '');
    } else {
      set({ impersonatedUserId: null, impersonatedUserEmail: null });
      try {
        sessionStorage.removeItem(SESSION_KEY_UID);
        sessionStorage.removeItem(SESSION_KEY_EMAIL);
      } catch { /* sessionStorage unavailable */ }
      console.log('[Auth] Impersonation cleared');
    }
    // If the effective user changed, clear all user-specific caches and reload
    if (prev !== userId) {
      clearUserCachesOnImpersonationChange();
    }
  },

  setSelectedAdmin: async (userId, email) => {
    const prev = get().selectedAdminUserId;
    const user = get().user;
    const extensionName = useAppStore.getState().extensionName;
    const appStore = useAppStore.getState();
    const orgId = appStore.appConfig?.orgId;
    const saasCode = appStore.appCodeSAAS;
    const adminList = get().invitedAdminList;
    const writeEntries: { label: string; path: string; promise: Promise<void> }[] = [];

    // Resolve the admin's real UID upfront so the self-share guard below
    // sees the actual UID (not the lookup key, which can be the email-id
    // form when the entry is sourced from `invitedAdminList`).
    const resolvedAdminUid = userId ? (adminList?.[userId]?.invitedAdminUserID ?? userId) : null;

    // Self-share prevention: if the resolved admin UID is the current user's
    // own UID, treat it as "no admin selected" — the user is effectively
    // looking at their own data and shouldn't be flagged as a shared user.
    // Falls through to the `else` (clear) branch below.
    if (resolvedAdminUid && user?.uid && resolvedAdminUid === user.uid) {
      console.log('[Auth] setSelectedAdmin: resolved admin UID matches current user — treating as clear');
      userId = null;
    }

    if (userId) {
      // Get the admin's real UID from the fetched list data
      const adminUid = adminList?.[userId]?.invitedAdminUserID ?? userId;
      const adminEmail = email ?? adminList?.[userId]?.invitedAdminUserEmail ?? null;
      // Stamp WHICH invite was accepted. `addUserToOrg` rewrites createdTime
      // on every add, so if the admin later removes and re-adds this user the
      // live doc carries a newer stamp than this one and the next
      // verifySelectedAdminMembership() sends them back to the Accept button.
      const inviteTime = adminList?.[userId]?.createdTime ?? null;

      console.log('[Auth] setSelectedAdmin called with:', { userId, email, resolvedAdminUid: adminUid, resolvedAdminEmail: adminEmail, inviteTime, prev });

      set({ selectedAdminUserId: adminUid, selectedAdminUserEmail: adminEmail, selectedAdminResolved: true });
      // Persist to localStorage (sync — matches old app key format for backward compat)
      if (user && extensionName) {
        try {
          localStorage.setItem(
            `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${extensionName}_${user.uid}`,
            adminUid
          );
        } catch { /* */ }
      }
      writeAcceptedInviteTime(inviteTime);

      // 1. Personal settings
      const personalPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${user?.uid}`;
      writeEntries.push({ label: 'personalSettings', path: personalPath, promise: persistSelectedAdminToSettings(adminUid, adminEmail, inviteTime) });

      // 2. TPA tokens (so CF knows which admin's creds to use)
      const tpaPath = `ulgebraUsers/${user?.uid}/extensions/${extensionName}/tokens/tpa`;
      writeEntries.push({ label: 'tpaTokens', path: tpaPath, promise: persistAdminToTPATokens(adminUid, adminEmail) });

      // 3. Unmark old admin in uaOrgAdmins
      if (prev && prev !== adminUid && user?.email && extensionName) {
        const prevAdminUid = adminList?.[prev]?.invitedAdminUserID ?? prev;
        const unmarkPath = `uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins/${prevAdminUid}`;
        writeEntries.push({ label: `unmarkOldAdmin(${prevAdminUid})`, path: unmarkPath, promise: updateDocument(`uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins`, prevAdminUid, { currentSelected: false }) });
      }

      // 4. Mark new admin in uaOrgAdmins. Accepting also un-rejects: a
      //    `rejectAdminInvite` mark left on this doc would otherwise make the
      //    next verifySelectedAdminMembership() revoke the org on reload
      //    ("invite was rejected") — accept → reload → popup, forever.
      if (user?.email && extensionName) {
        const markPath = `uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins/${adminUid}`;
        writeEntries.push({ label: `markNewAdmin(${adminUid})`, path: markPath, promise: updateDocument(`uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins`, adminUid, { currentSelected: true, _ua_lic_adminUserID: adminUid, _ua_lic_adminUserEmail: adminEmail, inviteStatus: deleteField(), rejectedTime: deleteField() }) });
      }
      // Mirror in memory so the chooser / banner drop the "rejected" state
      // without waiting for the reload.
      set((s) => {
        const existing = s.invitedAdminList?.[adminUid];
        if (!existing?.inviteStatus) return {};
        return { invitedAdminList: { ...s.invitedAdminList, [adminUid]: { ...existing, inviteStatus: undefined, rejectedTime: undefined } } };
      });
    } else {
      console.log('[Auth] setSelectedAdmin clearing, prev:', prev);

      set({ selectedAdminUserId: null, selectedAdminUserEmail: null, selectedAdminResolved: true });
      if (user && extensionName) {
        try {
          localStorage.removeItem(
            `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${extensionName}_${user.uid}`
          );
        } catch { /* */ }
      }
      writeAcceptedInviteTime(null);

      // 1. Personal settings
      const personalPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${user?.uid}`;
      writeEntries.push({ label: 'personalSettings(clear)', path: personalPath, promise: persistSelectedAdminToSettings(null, null, null) });

      // 2. TPA tokens
      const tpaPath = `ulgebraUsers/${user?.uid}/extensions/${extensionName}/tokens/tpa`;
      writeEntries.push({ label: 'tpaTokens(clear)', path: tpaPath, promise: persistAdminToTPATokens(null, null) });

      // 3. Unmark old admin in uaOrgAdmins
      if (prev && user?.email && extensionName) {
        const prevAdminUid = adminList?.[prev]?.invitedAdminUserID ?? prev;
        const unmarkPath = `uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins/${prevAdminUid}`;
        writeEntries.push({ label: `unmarkOldAdmin(${prevAdminUid})`, path: unmarkPath, promise: updateDocument(`uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins`, prevAdminUid, { currentSelected: false }) });
      }
    }

    // Wait for ALL Firestore writes before reloading. Don't let a single
    // write rejection skip the CF token-claim update below — log and continue.
    console.log('[Auth] Firestore writes queued:', writeEntries.map(e => `${e.label} → ${e.path}`));
    const writeResults = await Promise.allSettled(writeEntries.map(e => e.promise));
    writeResults.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.warn('[Auth] Firestore write failed:', writeEntries[i].label, writeEntries[i].path, r.reason);
      }
    });
    console.log('[Auth] All Firestore writes settled');

    // Admin changed — update or clear token claims
    if (user && extensionName) {
      const tpaBase = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;
      try {
        const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
        if (userId) {
          // Switching to a new admin — set claims with new adminId.
          // Authorize against the SAME appCode the invite was found under
          // (sourceAppCode), not always `extensionName`. fetchInvitedAdminList
          // falls back to the `{tpa}forpage` collection when the per-SaaS one
          // is empty; if we then sent `extensionName` here, the CF's check
          // (`ulgebraUsers/{adminUid}/extensions/${appCode}/uaOrgUsers/
          // {email}`) would look at a doc the admin never wrote → rejected
          // with "Not authorized" and no claim minted. Falls back to
          // `extensionName` for entries without a recorded source.
          const adminUid = adminList?.[userId]?.invitedAdminUserID ?? userId;
          const updateAppCode = adminList?.[userId]?.sourceAppCode || extensionName;
          await callServiceAPI({
            appCode: updateAppCode,
            TPAService: tpaBase,
            action: 'UPDATE_USER_TOKEN_CLAIMS',
            _ua_lic_adminUserID: adminUid,
            headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
          });
          console.log('[Auth] Token claims updated for admin:', adminUid, 'appCode:', updateAppCode);
        } else {
          // Clearing admin selection — remove adminId from claims for both
          // the current per-SaaS extension and the shared `{tpa}forpage`
          // appCode, since dual-write places adminId under both.
          const pageAppCode = `${tpaBase}forpage`;
          const appCodes = extensionName === pageAppCode
            ? [pageAppCode]
            : [extensionName, pageAppCode];
          // Sequential, NOT Promise.all. The CLEAR CF does a read-modify-
          // write of the user's full customClaims (getUser → delete one
          // apps[appCode].adminId → setCustomUserClaims). Run in parallel
          // both calls read the same original claims and the last write
          // clobbers the other's deletion, leaving one appCode's adminId
          // behind (the per-extension key survived while only the
          // forpage key got cleared). Sequential calls each re-read after
          // the previous write commits, so every key is actually removed.
          for (const appCode of appCodes) {
            await callServiceAPI({
              appCode,
              TPAService: tpaBase,
              action: 'CLEAR_USER_TOKEN_CLAIMS',
              headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
            });
          }
          console.log('[Auth] Token claims cleared (admin deselected) for', appCodes);
        }
        const fbUser = await getFirebaseUser();
        if (fbUser) {
          await fbUser.getIdToken(true);
        }
      } catch (err) {
        console.error('[Auth] Failed to update/clear token claims:', err);
      }
    }

    // If the effective admin changed, clear caches and reload
    const current = get().selectedAdminUserId;
    if (prev !== current) {
      clearUserCachesOnImpersonationChange();
    }
  },

  setSelectedAdminSilent: (userId, email) => {
    const user = get().user;
    // Self-share prevention: if the silent set is asking for our own UID,
    // collapse to null. Mirrors the guard in setSelectedAdmin so a stale
    // localStorage cache or restored personal-settings doc can't reinstate
    // a self-shared state across reloads.
    if (userId && user?.uid && userId === user.uid) {
      console.log('[Auth][Silent] selectedAdmin matches current user — coercing to null');
      userId = null;
      email = null;
    }

    // Restore paths pass email=null (localStorage and token claims carry only
    // the uid), and legacy data can deliver the literal string 'null'. Treat
    // anything that doesn't look like an email as unknown, resolve from the
    // invite list when available, and never store junk — filters that need
    // the admin's email (useValidAgentEmails) stay inert while it is unknown.
    if (userId && !looksLikeEmail(email)) {
      const resolved = get().invitedAdminList?.[userId]?.invitedAdminUserEmail;
      email = looksLikeEmail(resolved) ? resolved : null;
    }

    set({ selectedAdminUserId: userId, selectedAdminUserEmail: email ?? null, selectedAdminResolved: true });

    // The restore paths (localStorage, personal settings, token claims) all
    // land here, and they can resolve either before or after the invite list.
    // Kicking the check from both sides means the removal is caught whichever
    // order they happen to arrive in.
    if (userId) void get().verifySelectedAdminMembership();

    // Also sync to localStorage for backward compat
    const extensionName = useAppStore.getState().extensionName;
    if (user && extensionName && userId) {
      try {
        localStorage.setItem(
          `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${extensionName}_${user.uid}`,
          userId
        );
      } catch { /* */ }

      // Check token claims and request update only if missing or stale (non-blocking).
      // Use the full `extensionName` so the cache check (`claims.apps[appCode].
      // adminId`) reads the same key the UPDATE write produces — otherwise the
      // cache check always misses and we'd fire the CF on every load.
      const tpaBase = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;
      const appCode = extensionName;
      console.log('[Auth][Silent] Checking token claims for appCode:', appCode, 'adminUid:', userId);
      getFirebaseUser().then((fbUser) => {
        if (!fbUser) {
          console.warn('[Auth][Silent] No Firebase user available, skipping token claim check');
          return;
        }
        return fbUser.getIdTokenResult();
      }).then((tokenResult) => {
        if (!tokenResult) return;
        const apps = tokenResult.claims.apps as Record<string, { adminId?: string }> | undefined;
        const existingAdminId = apps?.[appCode]?.adminId;
        console.log('[Auth][Silent] Current claims.apps:', JSON.stringify(apps), 'existingAdminId:', existingAdminId, 'expected:', userId);
        if (existingAdminId === userId) {
          console.log('[Auth][Silent] Token claim already valid, skipping CF call');
          return;
        }
        console.log('[Auth][Silent] Token claim missing/stale, calling UPDATE_USER_TOKEN_CLAIMS');
        const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
        return callServiceAPI({
          appCode,
          TPAService: tpaBase,
          action: 'UPDATE_USER_TOKEN_CLAIMS',
          _ua_lic_adminUserID: userId,
          headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
        }).then(() => {
          console.log('[Auth][Silent] CF returned, force refreshing token');
          return getFirebaseUser();
        }).then((currentUser) => {
          if (currentUser) return currentUser.getIdToken(true);
        }).then(() => {
          console.log('[Auth][Silent] Token refreshed successfully for admin:', userId);
          set({ tokenClaimsVersion: get().tokenClaimsVersion + 1 });
        });
      }).catch((err) => {
        console.error('[Auth][Silent] Token claims update failed:', err);
      });
    }
  },

  fetchInvitedAdminList: async (opts) => {
    // Skip if already fetched this session — but a failed attempt must stay
    // retryable, or the admin-email backfill is dead for the whole session.
    if (!opts?.force && get().invitedAdminList !== null && !_invitedAdminFetchFailed) return;

    const user = get().user;
    const extensionName = useAppStore.getState().extensionName;
    if (!user?.email || !extensionName) return;
    // Reset only once we're actually going to fetch — clearing it before the
    // guard would let a guarded-out call (extensionName momentarily empty)
    // consume the retry flag and leave the backfill dead for the session.
    _invitedAdminFetchFailed = false;

    try {
      // Try the per-SaaS extension first; for page-family TPAs
      // (whatsapp/twilio/messagebird/routemobilewa) fall back to `{tpa}forpage`
      // when the primary collection is empty — invites added under the
      // standalone flow aren't duplicated into the per-SaaS collection.
      // See app-users-store addUserToOrg dual-write.
      const colPath = `uaOrgUsersEmailMap/${user.email}/extensions/${extensionName}/uaOrgAdmins`;
      let docs = await queryDocuments<InvitedAdmin & { id: string }>(colPath);
      // Track which appCode these docs actually came from so accept can
      // authorize against the same one (see InvitedAdmin.sourceAppCode).
      let sourceAppCode = extensionName;
      console.log('[Auth] uaOrgAdmins primary', { colPath, count: docs.length });

      if (docs.length === 0) {
        const pageMatch = /^(whatsapp|messagebird|twilio|routemobilewa)for/.exec(extensionName);
        const pageExt = pageMatch && extensionName !== `${pageMatch[1]}forpage`
          ? `${pageMatch[1]}forpage`
          : null;
        if (pageExt) {
          const pagePath = `uaOrgUsersEmailMap/${user.email}/extensions/${pageExt}/uaOrgAdmins`;
          docs = await queryDocuments<InvitedAdmin & { id: string }>(pagePath);
          if (docs.length > 0) sourceAppCode = pageExt;
          console.log('[Auth] uaOrgAdmins page fallback', { pagePath, count: docs.length });
        }
      }

      const list: Record<string, InvitedAdmin> = {};
      const selfUid = user.uid;
      const selfEmail = (user.email || '').toLowerCase();
      let selfSkipped = 0;

      for (const d of docs) {
        // Key by invitedAdminUserID — the admin's real Firebase UID,
        // always present (set when admin creates the doc via currentUser.uid)
        const adminUid = d.invitedAdminUserID || d.id;

        // Self-share prevention: never list the current user as a
        // share-able admin. Matches selectedAdmin coercion in
        // setSelectedAdmin / setSelectedAdminSilent — keeps the UI,
        // selection state, and persistence consistent. Match by both
        // UID and (case-insensitive) email so that legacy entries
        // missing one or the other still get filtered.
        if (adminUid === selfUid) { selfSkipped++; continue; }
        const adminEmail = (d.invitedAdminUserEmail || '').toLowerCase();
        if (selfEmail && adminEmail && adminEmail === selfEmail) { selfSkipped++; continue; }

        list[adminUid] = {
          invitedAdminUserID: adminUid,
          invitedAdminUserEmail: d.invitedAdminUserEmail || '',
          invitedAdminUserName: d.invitedAdminUserName || '',
          invitedAdminUserPic: d.invitedAdminUserPic,
          currentSelected: d.currentSelected,
          createdTime: typeof d.createdTime === 'number' ? d.createdTime : undefined,
          inviteStatus: d.inviteStatus === 'rejected' ? 'rejected' : undefined,
          rejectedTime: typeof d.rejectedTime === 'number' ? d.rejectedTime : undefined,
          // Org ids arrive as numbers from some platforms — normalise to string.
          saasOrgID: (() => { const v = (d as { saasOrgID?: unknown }).saasOrgID; return typeof v === 'string' || typeof v === 'number' ? String(v) : undefined; })(),
          sourceAppCode,
        };
      }

      set({ invitedAdminList: list });
      console.log('[Auth] Fetched invited admin list:', Object.keys(list).length, 'admins', selfSkipped > 0 ? `(${selfSkipped} self entries filtered)` : '');

      // Backfill the admin email if a restore ran before this list arrived
      // (restores carry only the uid) — same both-orders pattern as the
      // membership check below.
      const { selectedAdminUserId: selUid, selectedAdminUserEmail: selEmail } = get();
      const backfill = selUid && !looksLikeEmail(selEmail) ? list[selUid]?.invitedAdminUserEmail : null;
      if (looksLikeEmail(backfill)) {
        set({ selectedAdminUserEmail: backfill });
      }

      // Earliest reliable moment to check for a removal: this runs on header
      // mount, well before the background tier. Deduped, so it's free when the
      // check has already run for this admin.
      void get().verifySelectedAdminMembership();
    } catch (err) {
      console.warn('[Auth] Failed to fetch invited admin list:', err);
      _invitedAdminFetchFailed = true;
      set({ invitedAdminList: {} });
    }
  },

  rejectAdminInvite: async (adminUid: string) => {
    const user = get().user;
    const extensionName = useAppStore.getState().extensionName;
    if (!adminUid || !user?.email || !extensionName) {
      throw new Error('Cannot reject invite — missing user or app context');
    }

    const mark = { inviteStatus: 'rejected' as const, rejectedTime: Date.now() };

    // Cover every appCode an add could have written to: the one the invite was
    // actually read from, the current extension, and the shared `{tpa}forpage`
    // that page-family TPAs dual-write. Deduped so we don't write twice.
    const tpaBase = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;
    const pageExt = `${tpaBase}forpage`;
    const appCodes = Array.from(new Set([
      get().invitedAdminList?.[adminUid]?.sourceAppCode || extensionName,
      extensionName,
      ...(extensionName === pageExt ? [] : [pageExt]),
    ]));

    // This doc — under the user's own email — is the single place the
    // rejection is recorded. Deliberately NOT mirrored onto the admin's
    // `ulgebraUsers/{adminUid}/…/uaOrgUsers/{email}` copy: that subtree holds
    // the admin's tokens and credentials, so an invited user can't write it.
    // The admin's list picks the status up by reading these docs instead
    // (see app-users-store `mergeInviteRejections`).
    //
    // `updateDocument` (updateDoc), never setDoc: a missing doc means there is
    // no invite on that appCode, and creating one would invent an invite that
    // was never sent.
    const writes = appCodes.map((appCode) => ({
      appCode,
      promise: updateDocument(
        `uaOrgUsersEmailMap/${user.email}/extensions/${appCode}/uaOrgAdmins`,
        adminUid,
        mark,
      ),
    }));

    const results = await Promise.allSettled(writes.map((w) => w.promise));
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        console.warn('[Auth] Reject: invite doc write failed for', writes[i].appCode, r.reason);
      }
    });
    // Only a total failure is fatal — an invite that exists on one appCode and
    // not the other is normal, and that miss surfaces as a rejected promise.
    if (!results.some((r) => r.status === 'fulfilled')) {
      throw new Error('Could not record the rejection');
    }

    // Update in memory so the banner drops the row without waiting for a reload.
    set((s) => {
      const existing = s.invitedAdminList?.[adminUid];
      if (!existing) return {};
      return { invitedAdminList: { ...s.invitedAdminList, [adminUid]: { ...existing, ...mark } } };
    });
    console.log('[Auth] Invite rejected for admin:', adminUid);

    // Rejecting the org you're currently operating in also means leaving it.
    // Reloads, so this is the last thing we do.
    if (get().selectedAdminUserId === adminUid) {
      await get().setSelectedAdmin(null, null);
    }
  },

  verifySelectedAdminMembership: async () => {
    const { user, selectedAdminUserId, impersonatedUserId } = get();
    // While impersonating, the invite docs belong to the signed-in admin's
    // email but `selectedAdminUserId` can belong to the impersonated session.
    // Comparing the two would revoke on a mismatch that isn't real.
    if (impersonatedUserId) return;
    if (!selectedAdminUserId || !user?.email) return;
    if (revokeInFlight) return;

    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // The teardown clears `ua_selected_admin_user_id` from the personal
    // settings doc, whose path needs the org context. Missing it here means an
    // early-boot trigger: revoking now would skip that write, and the next
    // load would restore the very admin we just removed. Bail and let a later
    // pass (settings restore, background tier) run the check instead.
    if (!appStore.appConfig?.orgId || !appStore.appCodeSAAS) return;

    // Only meaningful once an accept has stamped a time. Invites accepted
    // before this stamp existed have none and must never be revoked over it.
    // The stamp can also arrive late on a fresh device (personal settings seed
    // it), which is why it's part of the dedupe key below — its arrival earns
    // exactly one more read.
    const acceptedAt = readAcceptedInviteTime();
    const checkKey = `${user.uid}:${extensionName}:${selectedAdminUserId}:${acceptedAt ?? 'none'}`;
    if (lastMembershipCheck === checkKey) return;
    lastMembershipCheck = checkKey;

    // Read the invite doc directly rather than going through
    // `invitedAdminList`: that list only falls back to `{tpa}forpage` when the
    // per-SaaS collection comes back EMPTY, so an admin whose invite lives on
    // the other appCode can be absent from it while the user is still a real
    // member. Revoking costs the user their organization, so confirm against
    // the docs themselves — both appCodes, since adds dual-write.
    const tpaBase = extensionName.includes('for') ? extensionName.split('for')[0] : extensionName;
    const pageExt = `${tpaBase}forpage`;
    const appCodes = extensionName === pageExt ? [extensionName] : [extensionName, pageExt];

    let invite: { createdTime?: number; inviteStatus?: string } | null = null;
    try {
      for (const appCode of appCodes) {
        invite = await getDocument<{ createdTime?: number; inviteStatus?: string }>(
          `uaOrgUsersEmailMap/${user.email}/extensions/${appCode}/uaOrgAdmins`,
          selectedAdminUserId,
        );
        if (invite) break;
      }
    } catch (err) {
      // A failed read looks exactly like a deleted doc. Never revoke on it —
      // and clear the dedupe key so the next caller retries.
      console.warn('[Auth] Membership check read failed — leaving shared access as-is:', err);
      lastMembershipCheck = null;
      return;
    }

    // Re-read: the reads above are async and the user may have switched orgs
    // (or been revoked by a concurrent pass) while they were in flight.
    if (get().selectedAdminUserId !== selectedAdminUserId || revokeInFlight) return;

    let reason: string | null = null;
    if (!invite) {
      reason = 'admin removed this user from their organization';
    } else if (invite.inviteStatus === 'rejected') {
      // Rejected on another device (or the leave step below didn't finish).
      reason = 'invite was rejected';
    } else if (acceptedAt && typeof invite.createdTime === 'number' && invite.createdTime > acceptedAt) {
      reason = 'invite was re-issued after a removal — needs a fresh Accept';
    }
    if (!reason) return;

    console.warn(`[Auth] Revoking shared access to ${selectedAdminUserId}: ${reason}`);
    revokeInFlight = true;
    try {
      // Full teardown — localStorage, personal settings, TPA tokens, token
      // claims — then caches + reload.
      await get().setSelectedAdmin(null, null);
    } catch (err) {
      console.error('[Auth] Failed to revoke shared access:', err);
    } finally {
      revokeInFlight = false;
    }
  },

  updatePhoto: async (photoURL: string) => {
    const user = get().user;
    if (!user) throw new Error('No authenticated user');

    // Update Firebase Auth profile
    await updateUserProfile({ photoURL });

    // Update Firestore user doc
    await setDocument(COLLECTIONS.USERS, user.uid, { photoURL });

    // Update local state
    set({ user: { ...user, photoURL } });

    // Refresh server-side UA_MAIL_VS_ID cache and client caches so downstream
    // readers (notifications, team uid resolution, GET_USERDETAILS_FOR_FBUID)
    // see the fresh photoURL instead of serving stale cached data.
    // Fire-and-forget — don't block the UI on cache maintenance.
    get().syncProfileCache().catch(() => { /* already logged inside */ });
  },

  syncProfileCache: async () => {
    const user = get().user;
    if (!user) return;
    const appStore = useAppStore.getState();
    const extensionName = appStore.extensionName;
    if (!extensionName) return;

    // 1. Ask the server to refresh UA_MAIL_VS_ID for this user.
    try {
      const callServiceAPI = httpsCallable(functions, 'new_callServiceAuthorizedAPI');
      await callServiceAPI({
        appCode: extensionName,
        TPAService: extensionName.split('for')[0],
        action: 'REFRESH_USER_PROFILE_CACHE',
        headers: { Authorization: 'Bearer {{AUTHTOKEN}}' },
      });
    } catch (err) {
      console.warn('[Auth] REFRESH_USER_PROFILE_CACHE failed:', err);
    }

    // 2. Invalidate the client-side caches so the next fetchUserDetails() hit
    //    reads the fresh server value instead of a 24h-stale IDB entry.
    if (!user.email) return;
    try {
      const email = user.email.toLowerCase();
      // Lazy import to avoid an auth-store ↔ app-users-store cycle at load time.
      const [{ useAppUsersStore }, { idbCache }] = await Promise.all([
        import('@/stores/app-users-store'),
        import('@/lib/cache/idb-cache'),
      ]);
      useAppUsersStore.setState((s) => {
        const next = { ...s.userDetailsCache };
        delete next[email];
        return { userDetailsCache: next };
      });
      await idbCache.delete('org_data', `user_detail_${email}`).catch(() => {});
    } catch (err) {
      console.warn('[Auth] client-side user-details cache clear failed:', err);
    }
  },

  initializeAuth: () => {
    // ── 1. Cache check — only once (guarded by `initialized`) ──
    if (!get().initialized) {
      // Hand-off from the admin panel: `?impersonateUid=<uid>&impersonateEmail=<email>`
      // on the landing URL means "start this tab already in impersonation
      // mode for that user". We:
      //   1. clear per-domain caches (so the tab doesn't show stale data
      //      from any previous same-app session in this browser),
      //   2. write the session keys so the existing restore path below
      //      picks them up,
      //   3. strip the params from the URL so a refresh/share doesn't
      //      accidentally re-trigger the flow.
      // The server-side Cloud Functions are the real security barrier —
      // this URL convention is a UX hand-off, not an auth boundary.
      try {
        const url = new URL(window.location.href);
        const pendingUid = url.searchParams.get('impersonateUid');
        const pendingEmail = url.searchParams.get('impersonateEmail');
        const pendingClearCache = url.searchParams.get('clearCache');
        if (pendingUid) {
          if (pendingClearCache === '1') {
            // Clear per-domain caches (localStorage user-specific keys +
            // IndexedDB stores) so the new tab starts with the
            // impersonated user's fresh state.
            for (const key of USER_SPECIFIC_LS_KEYS) {
              try { localStorage.removeItem(key); } catch { /* */ }
            }
            import('@/lib/api/proxy').then(({ clearAllCache }) => { clearAllCache(); }).catch(() => {});
            import('@/lib/cache/idb-cache').then(({ idbCache }) => {
              idbCache.clear('org_data').catch(() => {});
              idbCache.clear('templates').catch(() => {});
              idbCache.clear('proxy_cache').catch(() => {});
            }).catch(() => {});
          }
          sessionStorage.setItem(SESSION_KEY_UID, pendingUid);
          if (pendingEmail) sessionStorage.setItem(SESSION_KEY_EMAIL, pendingEmail);
          else sessionStorage.removeItem(SESSION_KEY_EMAIL);
          url.searchParams.delete('impersonateUid');
          url.searchParams.delete('impersonateEmail');
          url.searchParams.delete('clearCache');
          window.history.replaceState(null, '', url.toString());
          console.log('[Auth] Impersonation handoff applied:', pendingUid);
        }
      } catch { /* URL / storage unavailable */ }

      // Restore impersonation from sessionStorage (survives page reload)
      try {
        const savedUid = sessionStorage.getItem(SESSION_KEY_UID);
        const savedEmail = sessionStorage.getItem(SESSION_KEY_EMAIL);
        if (savedUid) {
          set({ impersonatedUserId: savedUid, impersonatedUserEmail: savedEmail });
          console.log('[Auth] Restored impersonation from session:', savedUid, savedEmail ?? '');
        }
      } catch { /* sessionStorage unavailable */ }

      // Optimistic initialization: check localStorage for cached user
      // This allows us to show the app immediately while Firebase auth loads.
      // localStorage is synchronous — user is available on the very first render.
      try {
        const cachedUserStr = localStorage.getItem('ua_cache_user');
        const cachedTimestampStr = localStorage.getItem('ua_cache_user_timestamp');

        if (cachedUserStr && cachedTimestampStr) {
          const cachedTimestamp = parseInt(cachedTimestampStr, 10);
          const cacheAge = Date.now() - cachedTimestamp;
          const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours

          if (cacheAge < MAX_CACHE_AGE) {
            const cachedUser = JSON.parse(cachedUserStr) as AppUser;
            set({ user: cachedUser, loading: false, initialized: true });
            console.log('[Auth] Using cached user (age:', Math.round(cacheAge / 1000 / 60), 'min)');
          } else {
            console.log('[Auth] Cache expired (age:', Math.round(cacheAge / 1000 / 60 / 60), 'hours), ignoring');
            localStorage.removeItem('ua_cache_user');
            localStorage.removeItem('ua_cache_user_timestamp');
          }
        }
      } catch {
        // Cache invalid or unavailable, proceed normally
      }
    }

    // ── 2. Firebase listener — module-level singleton ──
    // Uses a module-level flag so the listener survives React StrictMode
    // cleanup cycles (effect → cleanup → effect). Without this, StrictMode
    // would unsubscribe the listener on cleanup and the `initialized` guard
    // would prevent re-subscribing on the second effect run.
    if (!authListenerActive) {
      authListenerActive = true;
      const usedCache = get().initialized; // true if cache was just applied above

      onAuthChange(async (firebaseUser) => {
        console.log('[Auth] Firebase auth callback fired, user:', firebaseUser ? firebaseUser.email : 'null', 'usedCache:', usedCache);

        if (firebaseUser) {
          const user = mapFirebaseUser(firebaseUser);
          user.isAdmin = ADMIN_UIDS.has(firebaseUser.uid);

          // If cached user already has Firestore fields, use them instantly
          // and defer the Firestore read to background (avoids blocking on every page load).
          // If NO cache (first login / cleared storage), await Firestore so fields are correct.
          const cachedState = get().user;
          const hasCachedFields = usedCache && cachedState?.uid === firebaseUser.uid;

          const applyFirestoreFields = (userData: { isAdmin?: boolean; inAppCodes?: string[]; preferredCurrency?: string; useZohoFixedIp?: boolean } | null) => {
            if (!userData) return;
            if (userData.isAdmin) user.isAdmin = true;
            if (userData.inAppCodes) user.inAppCodes = userData.inAppCodes;
            if (userData.preferredCurrency) user.preferredCurrency = userData.preferredCurrency;
            if (userData.useZohoFixedIp) user.useZohoFixedIp = true;
          };

          // Check email verification for email/password users (Google users are always verified)
          const isEmailProvider = firebaseUser.providerData.some((p) => p.providerId === 'password');
          const emailNotVerified = isEmailProvider && !firebaseUser.emailVerified;

          // Start the user doc read now (shared with the background sync), so the
          // fixed-IP flag is known before the first Zoho calls; they wait for it.
          const userDocPromise = loadUserDocOnce(firebaseUser.uid);
          setZohoFixedIpReady(userDocPromise);

          if (hasCachedFields) {
            // Use cached fields instantly — no Firestore read at all.
            // Background sync happens later via use-app-init.ts step 10 (15s after page load).
            if (cachedState.isAdmin) user.isAdmin = true;
            if (cachedState.inAppCodes) user.inAppCodes = cachedState.inAppCodes;
            if (cachedState.preferredCurrency) user.preferredCurrency = cachedState.preferredCurrency;
            if (cachedState.useZohoFixedIp) user.useZohoFixedIp = true;
            set({ user, loading: false, initialized: true, needsEmailVerification: emailNotVerified });
            // The cached flag may be stale (support just turned it on or off).
            userDocPromise.then((userData) => {
              const current = get().user;
              if (!userData || current?.uid !== firebaseUser.uid) return;
              if (!!userData.useZohoFixedIp === !!current.useZohoFixedIp) return;
              const updated = { ...current, useZohoFixedIp: !!userData.useZohoFixedIp };
              set({ user: updated });
              try { localStorage.setItem('ua_cache_user', JSON.stringify(updated)); } catch { /* storage unavailable */ }
            }).catch(() => { /* the background sync reads it again */ });
          } else {
            // No cache — await Firestore read before setting user
            try {
              const userData = await userDocPromise;
              applyFirestoreFields(userData);
            } catch { /* Non-critical */ }
            set({ user, loading: false, initialized: true, needsEmailVerification: emailNotVerified });
          }

          // Validate restored impersonation — clear if the signed-in user is not an admin
          const restoredImpersonation = get().impersonatedUserId;
          if (restoredImpersonation && !ADMIN_UIDS.has(firebaseUser.uid)) {
            console.warn('[Auth] Non-admin has impersonation state — clearing');
            set({ impersonatedUserId: null, impersonatedUserEmail: null });
            try {
              sessionStorage.removeItem(SESSION_KEY_UID);
              sessionStorage.removeItem(SESSION_KEY_EMAIL);
            } catch { /* */ }
          }

          // Cache user to localStorage for optimistic next load (with timestamp)
          try {
            localStorage.setItem('ua_cache_user', JSON.stringify(user));
            localStorage.setItem('ua_cache_user_timestamp', Date.now().toString());
          } catch {
            // localStorage may be unavailable
          }
        } else {
          console.log('[Auth] No Firebase user, clearing state');
          setZohoFixedIpReady(); // no user doc to wait for
          set({ user: null, loading: false, initialized: true });
          // NOTE: Don't clear localStorage cache here — signOut() already handles it.
          // Firebase's onAuthStateChanged can briefly fire with null during token
          // refresh or page load before resolving the actual user, which would
          // wipe the cache and defeat the optimistic loading optimization.
        }
      });
    }

    // Return a no-op — the listener is managed at module level, not by React effects.
    // This prevents StrictMode cleanup from killing the auth listener.
    return () => {};
  },
}));

// Route Zoho calls through the `_fixedip` functions for users flagged on ulgebraUsers/{uid}.
useAuthStore.subscribe((state) => setZohoFixedIpForUser(!!state.user?.useZohoFixedIp));

/**
 * Return the effective user ID for **read-only** Firestore operations.
 *
 * Priority:
 *  1. Admin impersonation (debugging) → impersonatedUserId
 *  2. Shared admin access (app user using admin's creds) → selectedAdminUserId
 *  3. Real user → user.uid
 *
 * This covers all Firestore document paths, RTDB listeners, and module
 * `userUid` getters so they automatically resolve to the correct UID.
 */
export function getEffectiveUid(): string | null {
  const { user, impersonatedUserId, selectedAdminUserId } = useAuthStore.getState();
  if (!user) return null;
  return impersonatedUserId ?? selectedAdminUserId ?? user.uid;
}

/**
 * UID for **write** operations whose Firestore path embeds a user id.
 *
 * Mirrors `getEffectiveUid()` for admin impersonation — writes land on the
 * impersonated user's doc (the whole point of impersonation is to repair
 * that user's settings). Deliberately does NOT fall through to
 * `selectedAdminUserId`: shared-admin users own their own preferences and
 * shouldn't overwrite the admin's.
 */
export function getWriteUid(): string | null {
  const { user, impersonatedUserId } = useAuthStore.getState();
  if (!user) return null;
  return impersonatedUserId ?? user.uid;
}

/**
 * Clear all user-specific cached data when impersonation changes.
 * Resets zustand stores and localStorage caches, then triggers a full page
 * reload so every hook/listener re-initialises with the new effective UID.
 */
function clearUserCachesOnImpersonationChange(): void {
  console.log('[Auth] Clearing user-specific caches for impersonation change…');

  // 1. Clear user-specific localStorage keys
  for (const key of USER_SPECIFIC_LS_KEYS) {
    try { localStorage.removeItem(key); } catch { /* */ }
  }

  // 2. Drop the IndexedDB caches. `org_data`, `templates` and `proxy_cache`
  //    are keyed by extensionName with no uid in the key (`senders_${ext}`,
  //    `admin_details_${uid}`, templates(ext, channel), CRM record context),
  //    so a reload alone re-serves the PREVIOUS admin's senders, templates and
  //    conversations — which is how a removed user keeps seeing their old
  //    organization. Same three stores sign-out clears, for the same reason.
  //
  //    Bounded: openDB() has no onblocked handler, so a stale connection in
  //    another tab could leave this pending forever and the reload would never
  //    fire. Losing the clear is better than losing the reload.
  const reload = () => {
    // Short delay so the zustand state update (setImpersonation /
    // setSelectedAdmin) has time to flush to session/localStorage first.
    setTimeout(() => { window.location.reload(); }, 100);
  };
  import('@/lib/cache/idb-cache')
    .then(({ idbCache }) => Promise.race([
      Promise.all([
        idbCache.clear('org_data').catch(() => {}),
        idbCache.clear('templates').catch(() => {}),
        idbCache.clear('proxy_cache').catch(() => {}),
      ]),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]))
    .catch(() => { /* non-fatal — the reload is what actually matters */ })
    .finally(reload);
}

// Helper to map Firebase User to AppUser
function mapFirebaseUser(fbUser: {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  phoneNumber: string | null;
  metadata: { creationTime?: string };
}): AppUser {
  return {
    uid: fbUser.uid,
    email: fbUser.email || '',
    displayName: fbUser.displayName || fbUser.email || 'User',
    photoURL: fbUser.photoURL || undefined,
    phoneNumber: fbUser.phoneNumber || undefined,
    createdAt: fbUser.metadata.creationTime
      ? new Date(fbUser.metadata.creationTime)
      : new Date(),
  };
}

/**
 * Persist selectedAdminUserId to the personal settings Firestore doc.
 * Uses the same doc path as settings-store (uaAppPreferences/…/personal/{uid}).
 * Fire-and-forget — errors are logged but don't block the UI.
 */
async function persistSelectedAdminToSettings(
  adminUserId: string | null,
  adminEmail: string | null,
  inviteTime: number | null
): Promise<void> {
  try {
    const { doc: firestoreDoc, setDoc } = await import('firebase/firestore');
    const { db } = await import('@/config/firebase');
    const appStore = useAppStore.getState();
    const uid = useAuthStore.getState().user?.uid;
    const extensionName = appStore.extensionName;
    const orgId = appStore.appConfig?.orgId;
    const saasCode = appStore.appCodeSAAS;

    if (!uid || !extensionName || !saasCode || !orgId) {
      console.warn('[Auth] Skipping personal settings write — missing context:', { uid: !!uid, extensionName, orgId, saasCode });
      return;
    }

    const docPath = `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${uid}`;
    const docRef = firestoreDoc(db, docPath);
    await setDoc(docRef, {
      ua_selected_admin_user_id: adminUserId ?? null,
      ua_selected_admin_user_email: adminEmail ?? null,
      ua_selected_admin_invite_time: inviteTime ?? null,
    }, { merge: true });
    console.log('[Auth] Persisted selectedAdmin to personal settings:', adminUserId ?? 'cleared');
  } catch (err) {
    console.warn('[Auth] Failed to persist selectedAdmin to settings:', err);
  }
}

/**
 * Save _ua_lic_adminUserID to the TPA credentials doc so the Cloud Function
 * knows which admin's credentials to load.
 * Path: ulgebraUsers/{uid}/extensions/{extensionName}/tokens/tpa
 * Matches old app: saveAPIKeyInCredentials('tpa', '__multiple_keys__', saveObj)
 * Fire-and-forget — errors are logged but don't block the UI.
 */
async function persistAdminToTPATokens(
  adminUserId: string | null,
  adminEmail: string | null
): Promise<void> {
  try {
    const { doc: firestoreDoc, setDoc } = await import('firebase/firestore');
    const { db } = await import('@/config/firebase');
    const uid = useAuthStore.getState().user?.uid;
    const extensionName = useAppStore.getState().extensionName;

    if (!uid || !extensionName) return;

    const tokenDocRef = firestoreDoc(
      db, COLLECTIONS.USERS, uid, 'extensions', extensionName, 'tokens', 'tpa'
    );
    await setDoc(tokenDocRef, {
      _ua_lic_adminUserID: adminUserId ?? null,
      _ua_lic_adminUserEmail: adminEmail ?? null,
    }, { merge: true });
    console.log('[Auth] Persisted adminUserID to TPA tokens:', adminUserId ?? 'cleared');
  } catch (err) {
    console.warn('[Auth] Failed to persist adminUserID to TPA tokens:', err);
  }
}

// Ensure user document exists in Firestore
/**
 * Get the real Firebase Auth User object (with getIdToken, getIdTokenResult, etc.).
 * If Firebase Auth hasn't initialized yet (e.g. app loaded from cache), waits for it.
 */
function getFirebaseUser(): Promise<import('firebase/auth').User | null> {
  if (firebaseAuth.currentUser) return Promise.resolve(firebaseAuth.currentUser);
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(firebaseAuth, (user) => {
      unsub();
      resolve(user);
    });
  });
}

async function ensureUserDoc(user: AppUser): Promise<void> {
  try {
    const existing = await getDocument<{ emailDomain?: string }>(COLLECTIONS.USERS, user.uid);
    const emailDomain = user.email?.split('@')[1]?.toLowerCase() || null;
    if (!existing) {
      await setDocument(COLLECTIONS.USERS, user.uid, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL || null,
        createdAt: user.createdAt.toISOString(),
        emailDomain,
      });
    } else if (emailDomain && !existing.emailDomain) {
      await setDocument(COLLECTIONS.USERS, user.uid, { emailDomain });
    }
  } catch {
    // Non-critical, continue
  }
}

// Expose store for E2E testing — lets Playwright call signIn() directly
// when the inline login form is shown inside an iframe (window.self override
// breaks Playwright's fill/click, so we bypass the form entirely).
// Also exposed in VITE_E2E *production* builds (docgen serves a `vite preview`
// build because the dev server is unstable here) — VITE_E2E is false in real
// production builds, so this never ships to users.
if (import.meta.env.DEV || import.meta.env.VITE_E2E) {
  (window as any).__authStore = useAuthStore;
}
